import { IoSearchOutline } from 'react-icons/io5';
import { FiDownload } from 'react-icons/fi';
import React, { useState, useEffect, useContext } from 'react';
import Button from '../../components/shared/custom-btn';
import Heading from '../../components/shared/heading';
import DateRangePicker from '../../components/shared/date-range-picker';
import Modal from '../../components/shared/modal';
import { AccountsContext } from '../../components/context/account-context';
import { getVehiclesMinimal } from '../../services/vehicles-service';
import { getAllVendorsMinimal } from '../../services/vendors-service';
import logo from '../../assets/images/logo.jpg';
import { CSVLink } from 'react-csv';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getDateRangeUTC, formatUTCTimestampToLocal } from '../../utils/set-date-range-utils';
import { format } from 'date-fns';
import { getAllRenewals, getAllRenewalTypes } from '../../services/renewal-service';
import RenewalTable from './renewal-table';
import AddModal from './add-modal';
import { camelCaseToWords } from '../../utils/structure-words-utils';
import Help from './help';
import { usePageHelp } from '../../components/context/help-context';

const Renewals = () => {
    usePageHelp(Help);
    const { accountId } = useContext(AccountsContext);
    const [searchTerm, setSearchTerm] = useState('');
    const [openAddModal, setOpenAddModal] = useState(false);
    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
        endDate: new Date(),
    });
    const [newlyAddedItem, setNewlyAddedItem] = useState(null);
    const [renewalData, setRenewalData] = useState([]);
    const [error, setError] = useState(null);
    const [vehicles, setVehicles] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [typeOptions, setTypeOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [globalDataloading, setGlobalDataLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState({});


    const fetchGlobalData = async () => {
        if (!accountId) return;
        setGlobalDataLoading(true);

        try {
            const [vehiclesRes, vendorsRes, typesRes] = await Promise.all([
                getVehiclesMinimal(accountId),
                getAllVendorsMinimal(accountId),
                getAllRenewalTypes(accountId),
            ]);

            if (vehiclesRes.success) setVehicles(vehiclesRes.data.data || []);
            if (vendorsRes.success) setVendors(vendorsRes.data.data || []);
            if (typesRes.success) setTypeOptions(typesRes.data.data || []);
        } catch (err) {
            console.error("Failed to fetch global data", err);
            setError('Failed to fetch global data');
        } finally {
            setGlobalDataLoading(false);
        }
    };

    const fetchRenewals = async () => {
        setLoading(true);
        try {
            const { from, to } = getDateRangeUTC(dateRange);

            const response = await getAllRenewals(accountId, from, to);
            console.log(response)

            if (response.success) {
                const sortedData = response.data.data.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn)); // sort latest first
                setRenewalData(sortedData);
                setError(null);
            } else {
                setError(response.data.message || 'Failed to fetch Renewals records');
            }
        } catch (err) {
            setError('An error occurred while fetching Renewals records');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRenewals();
    }, [accountId, dateRange]);

    const handleOpenAddModal = () => {
        setOpenAddModal(true);
    };

    const handleCloseAddModal = () => {
        setOpenAddModal(false);
    };

    const handleAddItem = (newItem) => {
        setNewlyAddedItem(newItem);
        fetchRenewals();
    };

    const handleOpenEditModal = (item) => {
        setNewlyAddedItem(item);
    };

    const handleClearNewlyAddedItem = () => {
        setNewlyAddedItem(null);
    };

    // Filter data for export based on selected rows
    const exportData = Object.keys(selectedRows).length > 0
        ? renewalData.filter((_, index) => selectedRows[index])
        : renewalData;

    const csvData = exportData.map(item => ({
        'Renewal Id': item.renewalId || '',
        'Lineage Id': item.lineageId || '',
        Vehicle: item.vehicle?.name || '',
        Vendor: item.vendor?.name || '',
        Type: Array.isArray(item.type) ? item.type.join(', ') : camelCaseToWords(item.type) || '',
        'Start Time': formatUTCTimestampToLocal(item.startTime, 'yyyy-MM-dd hh:mm a'),
        'End Time': formatUTCTimestampToLocal(item.endTime, 'yyyy-MM-dd hh:mm a'),
        Remind: item.remind || '',
        Status: item.status || 'unknown',
        Note: item.note || '',
    }));

    const handleExportPDF = () => {
        const doc = new jsPDF();

        // Logo placement
        const logoWidth = 25;
        const logoHeight = 15;
        const pageWidth = doc.internal.pageSize.getWidth();
        const logoX = (pageWidth - logoWidth) / 2;
        const logoY = 2;

        doc.addImage(logo, 'PNG', logoX, logoY, logoWidth, logoHeight);

        // Title placement
        const title = 'Renewals Report';
        const titleY = logoY + logoHeight + 5;
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        const titleWidth = doc.getTextWidth(title);
        const titleX = (pageWidth - titleWidth) / 2;
        doc.text(title, titleX, titleY);

        // Date range placement
        const start = format(dateRange.startDate, 'yyyy-MM-dd');
        const end = format(dateRange.endDate, 'yyyy-MM-dd');
        const dateRangeText = `Date Range: ${start} to ${end}`;
        const dateRangeY = titleY + 5;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const dateRangeWidth = doc.getTextWidth(dateRangeText);
        const dateRangeX = (pageWidth - dateRangeWidth) / 2;
        doc.text(dateRangeText, dateRangeX, dateRangeY);

        // Table
        autoTable(doc, {
            head: [['Renewal Id', 'Lineage Id', 'Vehicle', 'Vendor', 'Type', 'Start Time', 'End Time', 'Remind', 'Status', 'Note']],
            body: exportData.map(item => [
                item.renewalId || '',
                item.lineageId || '',
                item.vehicle?.name || '',
                item.vendor?.name || '',
                Array.isArray(item.type) ? item.type.join(', ') : camelCaseToWords(item.type) || '',
                formatUTCTimestampToLocal(item.startTime, 'yyyy-MM-dd hh:mm a'),
                formatUTCTimestampToLocal(item.endTime, 'yyyy-MM-dd hh:mm a'),
                item.remind || '',
                item.status || 'unknown',
                item.note || '',
            ]),
            startY: dateRangeY + 5,
        });

        // Format filename with date range
        const fileName = `Renewals-${start}-to-${end}.pdf`;
        doc.save(fileName);
    };

    const isExportDisabled = Object.keys(selectedRows).length === 0;

    return (
        <div className="px-6 py-4">
            <div className='flex mb-2 items-center justify-between'>
                <div>
                    <Heading>Renewal History</Heading>
                </div>
                <Button
                    label='Add Renewal'
                    onClick={handleOpenAddModal}
                    className='text-white px-4 !py-1 rounded-lg bg-btnBg'
                />
            </div>

            <div className='space-y-4'>
                {error && (
                    <div className="text-red-600 bg-red-100 px-4 py-2 rounded-md">
                        {error}
                    </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex gap-3 flex-wrap">
                        <div className="bg-white rounded-md px-3 flex border border-border items-center gap-2 w-44 lg:w-72 py-1">
                            <IoSearchOutline className="text-gray-500 text-lg" />
                            <input
                                type="text"
                                className="bg-transparent outline-none py-1 w-full text-sm text-gray-700"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {/* <DateRangePicker
                            onChange={setDateRange}
                            initialRange={{
                                startDate: dateRange.startDate,
                                endDate: dateRange.endDate,
                            }}
                        /> */}
                    </div>

                    <div className="flex gap-2 items-center">
                        <Button
                            onClick={handleExportPDF}
                            className={` ${isExportDisabled ? '!cursor-not-allowed !opacity-75' : 'hover:!bg-gray-50'} flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md `}
                            label="PDF"
                            icon={<FiDownload className="text-gray-600" />}
                            iconPosition='left'
                            disabled={isExportDisabled}
                        />
                        <div
                            className={`flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md ${isExportDisabled ? 'cursor-not-allowed opacity-75' : 'hover:bg-gray-50'}`}
                        >
                            <CSVLink
                                data={csvData}
                                onClick={(event) => {
                                    if (isExportDisabled) {
                                        event.preventDefault();
                                    }
                                }}
                                fi
                                filename={`Renewals-${format(dateRange.startDate, 'yyyy-MM-dd')}-to-${format(dateRange.endDate, 'yyyy-MM-dd')}.csv`}
                                className={`${isExportDisabled ? 'cursor-not-allowed' : ''} flex items-center gap-1`}
                            >
                                <FiDownload className="text-gray-600" />
                                CSV
                            </CSVLink>
                        </div>
                    </div>
                </div>

                <RenewalTable
                    data={renewalData}
                    setData={setRenewalData}
                    globalFilter={searchTerm}
                    onGlobalFilterChange={setSearchTerm}
                    newlyAddedItem={newlyAddedItem}
                    setNewlyAddedItem={setNewlyAddedItem}
                    onClearNewlyAddedItem={handleClearNewlyAddedItem}
                    fetchRenewals={fetchRenewals}
                    loadingData={loading}
                    accountId={accountId}
                    vendors={vendors}
                    vehicles={vehicles}
                    types={typeOptions}
                    globalDataloading={globalDataloading}
                    fetchGlobalData={fetchGlobalData}
                    setSelectedRows={setSelectedRows}
                    selectedRows={selectedRows}
                />

                <Modal
                    isOpen={openAddModal}
                    onClose={handleCloseAddModal}
                    modalClass={'xl:w-[40%] lg:w-[45%] md:w-[65%] sm:w-[70%] w-[90%] !px-0'}
                >
                    <AddModal
                        handleCloseAddModal={handleCloseAddModal}
                        onAdd={handleAddItem}
                        openEditModal={handleOpenEditModal}
                        vendors={vendors}
                        vehicles={vehicles}
                        types={typeOptions}
                        context={'create'}
                        globalDataloading={globalDataloading}
                        fetchGlobalData={fetchGlobalData}
                    />
                </Modal>
            </div>
        </div>
    );
};

export default Renewals;